import { datadogRum } from "@datadog/browser-rum";
import { triggerAnalyticsEvent } from "./trigger-analytics-event";
import { TrackingAction, TrackingType } from "./types";

const MESSENGER_SELECTORS = [
  'iframe[name="intercom-launcher-frame"]',
  'iframe[name="intercom-messenger-frame"]',
  ".intercom-lightweight-app-launcher",
];

type MessengerTrigger = "initial_load" | "spa_navigation";

type MessengerType = "spotlight" | "classic" | "unknown";

let hasSeenFirstPageview = false;
let cancelPending: (() => void) | null = null;

// Returns true only when a Messenger element is present AND visible.
// Intercom sets visibility:hidden (not display:none) when hide_default_launcher
// is active, so we must check computed style, not just DOM presence.
function isMessengerVisible(): boolean {
  if (typeof window === "undefined") return false;
  return MESSENGER_SELECTORS.some((selector) => {
    const el = document.querySelector(selector);
    if (!el) return false;
    const style = window.getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
  });
}

// When an expected launcher never appears, classify why so the loaded-ratio can
// separate real faults from ad-blocker/network baseline:
// - not_booted: the Intercom SDK was never initialised on this route (the page
//   fired the pageview tracker but never mounted <IntercomMessenger>)
// - dom_absent: SDK booted but its widget never injected (script blocked)
// - launcher_hidden: widget present but the launcher element isn't visible
// Match only Intercom's own injected widget nodes — a substring class probe
// like [class*="intercom-"] would also match unrelated page classes and
// misreport dom_absent as launcher_hidden.
const INTERCOM_WIDGET_SELECTOR =
  '#intercom-container, #intercom-frame, .intercom-lightweight-app, iframe[name^="intercom-"]';

function detectFailureReason(): string {
  const intercom = (window as Window & { Intercom?: unknown }).Intercom;
  if (typeof intercom !== "function") return "not_booted";
  if (!document.querySelector(INTERCOM_WIDGET_SELECTOR)) return "dom_absent";
  return "launcher_hidden";
}

function detectMessengerType(): MessengerType {
  if (document.querySelector('iframe[name="intercom-messenger-frame"]')) {
    return "spotlight";
  }
  if (
    document.querySelector('iframe[name="intercom-launcher-frame"]') ||
    document.querySelector(".intercom-lightweight-app-launcher")
  ) {
    return "classic";
  }
  return "unknown";
}

function emit(
  platform: string,
  trigger: MessengerTrigger,
  isVisible: boolean,
  hiddenByDesign: boolean,
  reason?: string,
) {
  const messengerType = detectMessengerType();
  triggerAnalyticsEvent({
    action: TrackingAction.Visible,
    type: TrackingType.Element,
    text: "Messenger Visibility",
    analyticsId: "evt_messenger_loaded_visibility",
    location: "Intercom Messenger",
    metadata: {
      platform,
      isVisible,
      hiddenByDesign,
      trigger,
      messengerType,
      ...(reason ? { reason } : {}),
    },
  });

  if (isVisible && trigger === "initial_load") {
    datadogRum.addDurationVital("messenger_loaded", {
      startTime: performance.timeOrigin,
      duration: Math.round(performance.now()),
      context: { platform, isVisible, hiddenByDesign, trigger, messengerType },
    });
  }
}

/**
 * Tracks Messenger visibility on each pageview.
 * Emits a "Messenger Visibility" event with { platform, isVisible, hiddenByDesign, trigger }.
 * On an expected-but-failed load, metadata also carries a `reason` (see detectFailureReason).
 * A pending detection from the previous pageview is cancelled on each call.
 * Wire into onPageView so the correct pageview_id is stamped on the event.
 */
export function trackMessengerVisibility({
  platform,
  expected,
}: {
  platform: string;
  expected: boolean;
}) {
  if (typeof window === "undefined") return;

  cancelPending?.();
  cancelPending = null;

  const trigger: MessengerTrigger = hasSeenFirstPageview
    ? "spa_navigation"
    : "initial_load";
  hasSeenFirstPageview = true;

  if (!expected) {
    emit(platform, trigger, false, true);
    return;
  }

  if (isMessengerVisible()) {
    emit(platform, trigger, true, false);
    return;
  }

  let settled = false;

  function settle(isVisible: boolean) {
    if (settled) return;
    settled = true;
    observer.disconnect();
    clearTimeout(timeoutId);
    emit(
      platform,
      trigger,
      isVisible,
      false,
      isVisible ? undefined : detectFailureReason(),
    );
  }

  const observer = new MutationObserver(() => {
    if (isMessengerVisible()) settle(true);
  });

  observer.observe(document.body, { childList: true, subtree: true });

  const timeoutId = setTimeout(() => settle(false), 10000);

  cancelPending = () => {
    settled = true;
    observer.disconnect();
    clearTimeout(timeoutId);
  };
}
